/*
 * Copyright (c) 2016-2025 Martin Donath <martin.donath@squidfunk.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import {
  Observable,
  fromEvent,
  map,
  mergeMap,
  skip,
  switchMap,
  takeUntil,
  takeWhile
} from "rxjs"

import { getElements } from "~/browser"

/* ----------------------------------------------------------------------------
 * Helper functions
 * ------------------------------------------------------------------------- */

function getControlledNav(el: HTMLInputElement): HTMLElement | null {
  const parent = el.parentElement
  if (!parent)
    return null

  return parent.querySelector<HTMLElement>(":scope > nav")
}

function branchContainsActiveLink(el: HTMLInputElement): boolean {
  const nav = getControlledNav(el)
  return !!nav?.querySelector(".md-nav__link--active")
}

function applyDefaultState(el: HTMLInputElement): void {
  /* Always open the branch that contains the current active page */
  if (branchContainsActiveLink(el)) {
    el.classList.remove("md-toggle--indeterminate")
    el.indeterminate = false
    el.checked = true
    return
  }

  /* Preserve Material's default "expandable but not opened yet" behavior */
  if (el.classList.contains("md-toggle--indeterminate")) {
    el.indeterminate = true
    el.checked = false
    return
  }

  /* Everything else should start collapsed unless explicitly checked in HTML */
  el.indeterminate = false
}

/* ----------------------------------------------------------------------------
 * Helper types
 * ------------------------------------------------------------------------- */

/**
 * Patch options
 */
interface PatchOptions {
  document$: Observable<Document>      /* Document observable */
  tablet$: Observable<boolean>         /* Media tablet observable */
}

/* ----------------------------------------------------------------------------
 * Functions
 * ------------------------------------------------------------------------- */

/**
 * Patch indeterminate checkboxes
 *
 * This version intentionally does not persist navigation expand/collapse state
 * across page navigation. That persistence can desynchronize drawer state on
 * mobile and lead to seemingly random branches opening on browser back.
 *
 * Behavior:
 * - the current active branch is always opened
 * - indeterminate toggles keep Material's intended expandable behavior
 * - manual user toggles only affect the current page instance
 *
 * @param options - Options
 */
export function patchIndeterminate(
  { document$ }: PatchOptions
): void {
  document$
    .pipe(
      switchMap(() => {
        for (const el of getElements<HTMLInputElement>(".md-nav__toggle"))
          applyDefaultState(el)

        return getElements<HTMLInputElement>(".md-toggle--indeterminate")
      }),
      mergeMap(el => fromEvent(el, "change")
        .pipe(
          takeUntil(document$.pipe(skip(1))),
          takeWhile(() => el.classList.contains("md-toggle--indeterminate")),
          map(() => el)
        )
      )
    )
    .subscribe((el) => {
      const isTouchLike =
        navigator.maxTouchPoints > 0 ||
        window.matchMedia("(hover: none), (pointer: coarse)").matches

      el.classList.remove("md-toggle--indeterminate")
      el.indeterminate = false

      /*
       * On desktop, the old pseudo-indeterminate state should collapse after
       * the first explicit interaction. On touch devices we keep the checked
       * state produced by the user's tap.
       */
      if (!isTouchLike)
        el.checked = false
    })

  document$
    .pipe(
      switchMap(() => getElements<HTMLInputElement>(".md-nav__toggle")),
      mergeMap(el => fromEvent(el, "change")
        .pipe(
          takeUntil(document$.pipe(skip(1))),
          map(() => el)
        )
      )
    )
    .subscribe((el) => {
      /* Once the user interacted, this is a normal checkbox state */
      if (el.classList.contains("md-toggle--indeterminate")) {
        el.classList.remove("md-toggle--indeterminate")
      }
      el.indeterminate = false
    })
}